import React, { useEffect, useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Controller, UseFormReturn, useWatch } from 'react-hook-form'
import { FrontTabletRadioButton } from './input/radio-button'
import { FrontTabletInputField } from './input/input-field'
import { z } from 'zod'
import { SearchItem, SearchSetting, SearchItemType } from '@/models/search-setting'
import { FrontTabletFormLabel } from './input/form-label'
import {
  createSelfCheckinBaseStyle,
  createSelfCheckinResponsiveStyle,
  FrontTabletResponsiveToken,
} from '@/utils/front-tablet-responsive-style'

export const formSchema = z
  .object({
    mode: z.nativeEnum(SearchItem),
    tel: z.string().optional(),
    reservationId: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    firstNameKana: z.string().optional(),
    lastNameKana: z.string().optional(),
    keyNumber: z.string().optional(),
    roomNumber: z.string().optional(),
  })
  .refine(data => {
    switch (data.mode) {
      case SearchItem.Telephone:
        return data.tel
      case SearchItem.ReservationId:
        return data.reservationId
      case SearchItem.GuestName:
        return data.firstName && data.lastName
      case SearchItem.GuestNameKana:
        return data.firstNameKana && data.lastNameKana
      case SearchItem.QR:
        return true
      case SearchItem.RoomKey:
        return data.keyNumber && data.roomNumber
    }
  })
export type FormType = z.infer<typeof formSchema>

export const ReservationSearchForm: React.FC<{
  formReturn: UseFormReturn<FormType, any>
  searchItems: SearchSetting[]
}> = props => {
  const { t, i18n } = useTranslation()
  const { control, register, setValue } = props.formReturn

  const watchSearchMode = useWatch({ control, name: 'mode' })

  const viewSearchItems = useMemo(() => {
    return props.searchItems
      .filter(item => {
        return i18n.language === 'ja' || item.inputType !== 'guestNameKana'
      })
      .map(item => {
        switch (item.inputType) {
          case SearchItem.Telephone:
            return {
              inputType: item.inputType,
              label: 'The phone number of your reservation',
              input: function render() {
                return <FrontTabletInputField {...register('tel')} type="tel" placeholder="08012345678" pattern="^[0-9]+$" />
              },
              inputClass: 'text-field',
            }
          case SearchItem.ReservationId:
            return {
              inputType: item.inputType,
              label: 'Reservation ID',
              input: function render() {
                return <FrontTabletInputField {...register('reservationId')} type="text" placeholder="aipass001" />
              },
              inputClass: 'text-field',
            }
          case SearchItem.GuestName:
            return {
              inputType: item.inputType,
              label: 'Reservation name or guest name',
              input: function render() {
                return (
                  <>
                    <div className="split-text-field">
                      <FrontTabletInputField {...register('lastName')} type="text" placeholder={t('Last name')} />
                      <FrontTabletInputField {...register('firstName')} type="text" placeholder={t('First name')} />
                    </div>
                    {i18n.language !== 'ja' && <p className="split-text-annotation">{t('fullNameInputHint')}</p>}
                  </>
                )
              },
              inputClass: 'non-style',
            }
          case SearchItem.GuestNameKana:
            return {
              inputType: item.inputType,
              label: 'Reservation name or guest name (Furigana)',
              input: function render() {
                return (
                  <>
                    <FrontTabletInputField {...register('lastNameKana')} type="text" placeholder={t('Last name Furigana')} />
                    <FrontTabletInputField {...register('firstNameKana')} type="text" placeholder={t('First name Furigana')} />
                  </>
                )
              },
              inputClass: 'split-text-field',
            }
          case SearchItem.QR:
            return {
              inputType: item.inputType,
              label: 'Check-in QR',
              input: undefined,
              inputClass: '',
            }
          case SearchItem.RoomKey:
            return {
              inputType: item.inputType,
              label: 'Room / Key number',
              input: function render() {
                return (
                  <>
                    <FrontTabletFormLabel label={t('Room number')}>
                      <FrontTabletInputField {...register('roomNumber')} type="text" inputMode="numeric" placeholder="301" />
                    </FrontTabletFormLabel>
                    <FrontTabletFormLabel label={t('Key number')}>
                      <FrontTabletInputField {...register('keyNumber')} type="text" inputMode="numeric" placeholder="123456" />
                    </FrontTabletFormLabel>
                  </>
                )
              },
              inputClass: 'split-text-field',
            }
        }
      })
  }, [props.searchItems, i18n.language])

  const onChangeSearchMode = (v: SearchItemType) => {
    setValue('mode', v || viewSearchItems[0].inputType)
  }

  useEffect(() => {
    if (i18n.language !== 'ja' && watchSearchMode === 'guestNameKana') {
      setValue('mode', viewSearchItems[0].inputType)
    }
    if (!watchSearchMode) {
      setValue('mode', viewSearchItems[0].inputType)
    }
  }, [watchSearchMode, i18n.language])

  return (
    <div css={componentStyle}>
      {viewSearchItems.map(item => (
        <React.Fragment key={item.inputType}>
          <div className={`input-block${watchSearchMode === item.inputType ? ' is-selected' : ''}`}>
            <Controller
              name="mode"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <FrontTabletRadioButton
                  id={`input-mode-${item.inputType}`}
                  value={item.inputType}
                  label={t(item.label)}
                  name={name}
                  formValue={value}
                  onChange={e => {
                    onChange(e)
                    onChangeSearchMode(e.currentTarget.value)
                  }}
                  itemCss={{ width: 'fit-content' }}
                  radioSizeType="normal"
                  radioLabelSizeType="small"
                />
              )}
            />
            {watchSearchMode === item.inputType && <div className={item.inputClass}>{item.input && item.input()}</div>}
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

const componentStyle = css(
  {
    '.input-block': {
      display: 'flex',
      flexDirection: 'column',
      padding: '18px 32px 17px',
      borderTop: '1px solid #f2f2f2',
      ':first-of-type': { border: 'none' },
      '&.is-selected': { padding: '16px 32px 18px' },
      '.text-field, .split-text-field, .split-text-annotation': {
        paddingTop: 11,
        paddingLeft: 64,
      },
      '.split-text-field': {
        display: 'flex',
        gap: 24,
        '>p': {
          gap: 24,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          whiteSpace: 'nowrap',
        },
      },
    },
  },
  createSelfCheckinBaseStyle({ '.input-block': { minHeight: 76 } }),
  createSelfCheckinResponsiveStyle({
    landscape13: {
      '.input-block': {
        minHeight: 88,
        padding: '24px 32px 23px',
        '&.is-selected': { padding: '24px 32px 26px' },
        '.text-field, .split-text-field, .split-text-annotation': { paddingTop: 12 },
        label: { fontSize: FrontTabletResponsiveToken.formLabelFontSize.landscape13 },
      },
    },
  }),
)
